import { Box, Flex, Text } from '@chakra-ui/react';
import { FaMicrophone } from '@react-icons/all-files/fa/FaMicrophone';
import { FaRegClock } from '@react-icons/all-files/fa/FaRegClock';
import { useState } from 'react';

import Loader from '../../components/Loader';
import Title from '../../components/Title';
import Button from '../../components/ui/buttons/Button';
import { isRecordingSupported } from '../../hooks/useVoiceRecorder';
import DesktopHandoff from './DesktopHandoff';
import FeedbackStatusCard from './FeedbackStatusCard';
import InAppBrowserPrompt from './InAppBrowserPrompt';
import VoiceQuestion from './VoiceQuestion';
import { isLikelyInAppBrowser, isTouchDevice } from './surveyEnvironment';
import useFeedbackSurvey from './useFeedbackSurvey';

const QUESTION_COUNT_WORDS: Record<number, string> = { 3: 'Three', 4: 'Four' };

const Intro = ({
  firstName,
  isCompletedSale,
  questionCount,
  onStart,
}: {
  firstName: string | null;
  isCompletedSale: boolean;
  questionCount: number;
  onStart: () => void;
}) => (
  <Flex direction="column" flex={1} w="full">
    <Flex direction="column" flex={1} justify="center" px={7}>
      <Text fontSize="44px" mb={4}>
        👋
      </Text>
      <Title fontSize="30px" lineHeight={1.15} textAlign="left" letterSpacing="-0.4px">
        {firstName ? `Hey ${firstName}, got two minutes?` : 'Hey there, got two minutes?'}
      </Title>
      <Text mt={3.5} fontSize="16.5px" lineHeight={1.6} color="leaseEndGray" fontWeight={500}>
        {isCompletedSale
          ? "We'd love to hear how selling your car went, in your own words."
          : "We'd love to hear what happened, in your own words."}{' '}
        {QUESTION_COUNT_WORDS[questionCount] ?? questionCount} quick questions, answered out loud.
        No typing, no wrong answers, and it genuinely helps us get better.
      </Text>
      <Flex gap={4.5} mt={5} color="taupeGray" fontSize="13.5px" fontWeight={600}>
        <Flex align="center" gap={1.5}>
          <FaRegClock />
          <Text>About 2 minutes</Text>
        </Flex>
        <Flex align="center" gap={1.5}>
          <FaMicrophone />
          <Text>Just your voice</Text>
        </Flex>
      </Flex>
    </Flex>
    <Box px={5} pb={7}>
      <Button w="full" textTransform="uppercase" leftIcon={<FaMicrophone />} onClick={onStart}>
        Start
      </Button>
      <Text textAlign="center" fontSize="12.5px" color="silver" mt={3.5} fontWeight={500}>
        We'll ask for mic access on the first question.
      </Text>
    </Box>
  </Flex>
);

const ThankYou = ({ firstName }: { firstName: string | null }) => (
  <FeedbackStatusCard
    emoji="💛"
    title={firstName ? `Thanks, ${firstName}.` : 'Thanks.'}
    body="This really helps. A real person reads every response, and what you told us shapes what we fix next."
    hideBackLink
  />
);

const InvalidLinkCard = () => (
  <FeedbackStatusCard
    emoji="🔗"
    title="This link isn't working"
    body="Double-check the link from your email, or head back to Lease End."
  />
);

const MarketplaceFeedback = () => {
  const {
    token,
    invite,
    loadFailed,
    retry,
    questions,
    firstName,
    isCompletedSale,
    phase,
    startSurvey,
    questionIndex,
    saveAnswer,
    isSaving,
    sendFailed,
  } = useFeedbackSurvey();

  const [inAppPromptDismissed, setInAppPromptDismissed] = useState(false);
  const needsBrowserHandoff = isLikelyInAppBrowser() || !isRecordingSupported();

  if (!token) return <InvalidLinkCard />;

  if (loadFailed) {
    return (
      <FeedbackStatusCard
        emoji="🤔"
        title="Something went wrong"
        body="We couldn't load your survey. Try refreshing the page, or tap below to retry."
        action={{ label: 'Try again', onClick: retry }}
      />
    );
  }

  if (!invite) return <Loader hideText mt={8} />;

  if (invite.state === 'invalid') return <InvalidLinkCard />;
  if (invite.state === 'expired') {
    return (
      <FeedbackStatusCard
        emoji="⏳"
        title="This link's expired"
        body="Feedback links stay open for 30 days, and this one's just past that. Thanks for stopping by."
      />
    );
  }
  if (invite.state === 'completed') {
    return (
      <FeedbackStatusCard
        emoji="✅"
        title={firstName ? `Thanks, ${firstName}, we've got it.` : "Thanks, we've got it."}
        body="You already shared your feedback on this one and it's safely with our team. You're all set."
      />
    );
  }

  if (!isTouchDevice()) return <DesktopHandoff firstName={firstName} />;

  const currentQuestion = questions[questionIndex];

  const renderPhase = () => {
    if (needsBrowserHandoff && !inAppPromptDismissed) {
      return <InAppBrowserPrompt onContinue={() => setInAppPromptDismissed(true)} />;
    }
    if (phase === 'done') return <ThankYou firstName={firstName} />;
    if (phase === 'questions' && currentQuestion) {
      return (
        <>
          <Box px={7} pt={5}>
            <Text
              fontSize="12.5px"
              fontWeight={700}
              letterSpacing="1px"
              textTransform="uppercase"
              color="silver"
            >
              Question {questionIndex + 1} of {questions.length}
            </Text>
          </Box>
          <VoiceQuestion
            key={currentQuestion.key}
            question={currentQuestion.text}
            subtitle={currentQuestion.subtitle}
            hasRating={currentQuestion.hasRating}
            isLastQuestion={questionIndex === questions.length - 1}
            isSaving={isSaving}
            sendFailed={sendFailed}
            onAccept={saveAnswer}
          />
        </>
      );
    }
    return (
      <Intro
        firstName={firstName}
        isCompletedSale={isCompletedSale}
        questionCount={questions.length}
        onStart={startSurvey}
      />
    );
  };

  return (
    <Flex direction="column" maxW="480px" mx="auto" w="full" minH="75vh">
      {renderPhase()}
    </Flex>
  );
};

export default MarketplaceFeedback;
